import { api } from "@/lib/api";

/**
 * Pending RFQ replay helper.
 *
 * When an anonymous shopper prices a trip on /quote and clicks
 * "Get Operator Direct Bids", we stash the FULL trip payload here before
 * routing them to /charter-membership. After they finish signing up +
 * paying, we replay the stash by calling POST /api/charter-requests on
 * their behalf so the trip is already sitting on their dashboard —
 * "working for them" the moment they land.
 *
 * The stash is intentionally in sessionStorage (not localStorage) so it
 * dies with the tab and never lingers.
 */

const KEY = "pjp_pending_rfq";

/**
 * Stash the payload the way POST /api/charter-requests wants it.
 * Callers on /quote just serialise their in-flight quote here.
 */
export function stashPendingRfq(payload) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ...payload, stashed_at: Date.now() }));
  } catch {
    // Private mode / quota — swallow; the customer just has to redo the form.
  }
}

export function peekPendingRfq() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    // Auto-expire after 12h so a stashed cart from 3 days ago doesn't fire.
    if (obj.stashed_at && Date.now() - obj.stashed_at > 12 * 3600 * 1000) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

export function clearPendingRfq() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

/**
 * Fire the stashed RFQ. Requires the caller to already be authenticated
 * (bearer token attached by axios interceptor). Contact fields are ALWAYS
 * patched from the current PJP user via GET /auth/me so we never send the
 * anonymous placeholder email to the server (Pydantic rejects .local TLDs
 * anyway). On success returns the created request id; on failure returns
 * null (caller may re-toast).
 */
export async function replayPendingRfq() {
  const p = peekPendingRfq();
  if (!p) return null;
  const { stashed_at, route_label, ...body } = p;
  try {
    // Overwrite anonymous placeholder contact fields with the real user's.
    const me = await api.get("/auth/me");
    const u = me.data || {};
    body.contact_name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || body.contact_name;
    body.contact_email = u.email || body.contact_email;
  } catch {
    // If /me fails we still try — but PaymentSuccess.jsx will have refreshed
    // the auth context first so the bearer token should be valid.
  }
  try {
    const r = await api.post("/charter-requests", body);
    clearPendingRfq();
    return r.data?.request_id || true;
  } catch {
    // Leave the stash so the user can try again later.
    return null;
  }
}
